import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const registerLocationPicker = () => {
    if (window.__locationPickerRegistered) {
        return;
    }

    window.__locationPickerRegistered = true;
    window.Alpine.data('locationPicker', (params) => window.locationPicker(params));
};

document.addEventListener('alpine:init', registerLocationPicker);

if (window.Alpine) {
    registerLocationPicker();
}

window.locationPicker = ({ locations, selected, statePath }) => ({
    // --- reactive state ---
    locations: Array.isArray(locations) ? locations : Object.values(locations ?? {}),
    filteredLocations: [],
    selected,
    statePath,
    map: null,
    markers: [],
    userMarker: null,
    addressQuery: '',
    addressResults: [],
    isMobile: window.innerWidth < 640,
    popupOffset: window.innerWidth < 640 ? -8 : -12, // centralized popup offset

    // --- Lazy init (ensures container visible) ---
    initDeferred() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    observer.disconnect();
                    this.init();
                }
            });
        }, { threshold: 0.2 });
        observer.observe(this.$el);
    },

    // --- Lifecycle ---
    init() {
        if (this._initialized && this.map) {
            return;
        }

        this.filteredLocations = this.locations;

        this.$nextTick(() => {
            this.initializeMap();

            if (!this.map) {
                return;
            }

            if (!this._initialized) {
                this._initialized = true;
                if (!this.selected) {
                    setTimeout(() => this.locateUser(), 1000);
                }

                if (!this._resizeListenerAttached) {
                    this._resizeListenerAttached = true;
                    window.addEventListener('resize', () => {
                        const nowMobile = window.innerWidth < 640;
                        if (nowMobile !== this.isMobile) {
                            this.isMobile = nowMobile;
                            this.popupOffset = nowMobile ? -8 : -12;
                            this.initializeMap();
                        } else {
                            this.map.invalidateSize();
                        }
                    });
                }
            }
        });

        if (this.selected) {
            this.$nextTick(() => this.selectLocation(this.selected, 13));
        }
    },

    // --- Map setup ---
    initializeMap() {
        const isMobile = window.innerWidth < 640;
        const mapId = isMobile ? '#map-mobile' : '#map-desktop';
        const mapEl = this.$el.querySelector(mapId);

        if (!mapEl) return;

        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        this.map = L.map(mapEl, {
            scrollWheelZoom: false,
            doubleClickZoom: true,
            dragging: !L.Browser.mobile,
            tap: !L.Browser.mobile
        }).setView([51.1657, 10.4515], 13);
        mapEl._leaflet_map_instance = this.map;

        const stadiaApiKey =
            window.appConfig?.stadiaApiKey ||
            import.meta.env.VITE_STADIA_KEY ||
            '';

        const baseUrl = 'https://tiles-eu.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png';
        const tileUrl = stadiaApiKey
            ? `${baseUrl}?language=de&api_key=${stadiaApiKey}`
            : `${baseUrl}?language=de`;

        L.tileLayer(tileUrl, {
            maxZoom: 19,
            attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | ' +
                '<a href="https://stadiamaps.com/">Stadia Maps</a>',
        }).addTo(this.map);

        this.renderMarkers();
        setTimeout(() => this.map.invalidateSize(), 400);
    },

    // --- Manual selection (list or marker) ---
    selectLocation(id, zoomOverride = null, setSelected = true) {
        const loc = this.locations.find(l => l.id === id);
        if (!loc || !this.map) return;

        if (setSelected) {

            this.selected = id;

            if (this.statePath && this.$wire) {
                this.$wire.set(this.statePath, id);
            }

            this.$nextTick(() => {
                this.scrollSelectedIntoView();
                setTimeout(() => this.scrollSelectedIntoView(), 50);
            });
        }

        const marker = this.markers.find(m => {
            const { lat, lng } = m.getLatLng();
            return Math.abs(lat - loc.lat) < 0.0001 && Math.abs(lng - loc.lng) < 0.0001;
        });

        // Calculate distance dynamically if user located
        if (!loc.distance && this.userMarker) {
            const { lat, lng } = this.userMarker.getLatLng();
            loc.distance = this.haversine(lat, lng, loc.lat, loc.lng);
        }

        const zoom = zoomOverride ?? 15;
        const offsetY = -60;
        const point = this.map.project([loc.lat, loc.lng], zoom);
        const shiftedLatLng = this.map.unproject(L.point(point.x, point.y - offsetY), zoom);

        this.map.flyTo(shiftedLatLng, zoom, { animate: true, duration: 0.8 });

        const popupHtml = `
            <b>${loc.name}</b><br>
            ${loc.address ?? ''}<br>
            ${loc.distance
            ? `<small style="color:#666;">${loc.distance.toFixed(1)} km entfernt</small>`
            : ''}
        `;

        (marker
                ? marker.bindPopup(popupHtml, { offset: [0, this.popupOffset] })
                : L.popup({ offset: [0, this.popupOffset] })
                .setLatLng([loc.lat, loc.lng])
                .setContent(popupHtml)
        ).openPopup();
    },

    // --- Scroll list to selected item ---
    scrollSelectedIntoView() {
        if (!this.selected) return;

        const desktopList = this.$el.querySelector('.sm\\:flex .flex-1.overflow-y-auto');
        const desktopItem = desktopList?.querySelector(`[data-loc-id="${this.selected}"]`);

        if (desktopList && desktopItem) {
            desktopItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        const mobileList = this.$el.querySelector('.sm\\:hidden .overflow-y-auto.h-full');
        const mobileItem = mobileList?.querySelector(`[data-loc-id="${this.selected}"]`);

        if (mobileList && mobileItem) {
            mobileItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    },

    // --- Render all markers ---
    renderMarkers() {
        this.markers.forEach(m => this.map.removeLayer(m));
        this.markers = [];

        const customMarker = L.divIcon({
            className: 'custom-marker',
            html: `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                     fill="rgb(119, 41, 190)" width="28" height="28">
                    <path fill-rule="evenodd"
                          d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742
                             19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0
                             c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742Z
                             M12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd"/>
                </svg>
            `,
            iconSize: [28, 28],
            iconAnchor: [14, 28],
            popupAnchor: [0, this.popupOffset],
        });

        if (!Array.isArray(this.filteredLocations)) return;

        this.filteredLocations.forEach((loc, idx) => {
            if (!loc.lat || !loc.lng) return;

            const marker = L.marker([loc.lat, loc.lng], { icon: customMarker })
            .addTo(this.map)
            .bindPopup(`
                    <b>${idx + 1}. ${loc.name}</b><br>
                    ${loc.address ?? ''}<br>
                    <small>${loc.distance ? loc.distance.toFixed(1) + ' km entfernt' : ''}</small>
                `, { offset: [0, this.popupOffset] })
            .on('click', () => this.selectLocation(loc.id));

            this.markers.push(marker);
        });

        if (this.markers.length > 0) {
            const group = L.featureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.1));
        }
    },

    // --- Haversine (distance km) ---
    haversine(lat1, lon1, lat2, lon2) {
        const R = 6371;
        const toRad = deg => deg * (Math.PI / 180);
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    // --- Locate user via browser API ---
    locateUser() {

        if (!navigator.geolocation) {
            console.warn('Geolocation not supported.');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;

                const userIcon = L.divIcon({
                    className: 'user-marker',
                    html: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgb(119, 41, 190)">
                             <circle cx="7" cy="7" r="6" opacity="0.25"/>
                             <circle cx="7" cy="7" r="3"/>
                           </svg>`,
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                });

                if (this.userMarker) this.map.removeLayer(this.userMarker);
                this.userMarker = L.marker([lat, lng], { icon: userIcon }).addTo(this.map);

                this.filteredLocations = this.locations.map(l => ({
                    ...l,
                    distance: this.haversine(lat, lng, l.lat, l.lng),
                })).sort((a, b) => a.distance - b.distance);

                this.renderMarkers();

                this.selectNearestLocation(lat, lng);
            },
            (err) => console.warn('Geolocation error:', err.message),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
    },

    // --- Find nearest and center map softly ---
    selectNearestLocation(lat, lng) {
        if (!this.locations?.length) return;

        let nearest = null;
        let minDist = Infinity;

        this.locations.forEach((loc) => {
            const dist = this.haversine(lat, lng, loc.lat, loc.lng);
            if (dist < minDist) {
                minDist = dist;
                nearest = loc;
            }
        });

        if (nearest) {
            // Only center map, don't select yet
            this.selectLocation(nearest.id, 11, false);
            return nearest;
        }

        return null;
    },

    // --- Search address and scroll to nearest location ---
    searchAddress() {
        if (!this.addressQuery || this.addressQuery.length < 3) {
            this.addressResults = [];
            return;
        }

        // Get API key from config
        const stadiaApiKey =
            window.appConfig?.stadiaApiKey ||
            import.meta.env.VITE_STADIA_KEY ||
            '';

        if (!stadiaApiKey) {
            console.warn('No Stadia API key available for geocoding');
            return;
        }

        // Show loading state
        this.addressResults = [{ display_name: 'Suche...', loading: true }];

        // Build the geocoding URL (using Nominatim API for OpenStreetMap geocoding)
        const geocodingUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.addressQuery)}&countrycodes=de&limit=5&accept-language=de`;

        // Fetch address results
        fetch(geocodingUrl, {
            headers: {
                'Accept-Language': 'de',
                'User-Agent': 'Vivatura Location Picker'
            }
        })
            .then(response => response.json())
            .then(data => {
                if (!data?.length) {
                    this.addressResults = [{ display_name: 'Keine Ergebnisse gefunden', notFound: true }];
                    return;
                }

                // Format results for display - use Nominatim result format
                this.addressResults = data.map(place => ({
                    place_id: place.place_id,
                    display_name: place.display_name,
                    lat: parseFloat(place.lat),
                    lon: parseFloat(place.lon)
                })).slice(0, 5); // Limit to top 5 results
            })
            .catch(error => {
                console.error('Geocoding error:', error);
                this.addressResults = [{ display_name: 'Fehler bei der Adresssuche', error: true }];
            });
    },

    // --- Select address from search results ---
    selectAddress(result) {
        if (!result || !result.lat || !result.lon) return;

        // Clear search results
        this.addressResults = [];

        // Clear search input
        this.addressQuery = '';

        // Create a temporary marker for the selected address
        const searchIcon = L.divIcon({
            className: 'search-marker',
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   fill="#3b82f6" width="28" height="28">
                    <path fill-rule="evenodd"
                         d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742
                         19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0
                         c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742Z
                         M12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd"/>
                </svg>`,
            iconSize: [28, 28],
            iconAnchor: [14, 28],
            popupAnchor: [0, this.popupOffset],
        });

        // Add temporary marker
        const searchMarker = L.marker([result.lat, result.lon], { icon: searchIcon })
            .addTo(this.map)
            .bindPopup(`
                <b>Gesuchte Adresse</b><br>
                ${result.display_name}
            `, { offset: [0, this.popupOffset] })
            .openPopup();

        // Fly to the search result
        this.map.flyTo([result.lat, result.lon], 14, { animate: true, duration: 0.8 });

        // Find nearest location to this address and select it
        setTimeout(() => {
            // Find the nearest location
            const nearest = this.selectNearestLocation(result.lat, result.lon);

            if (nearest) {
                // Actually select the location
                this.selected = nearest.id;

                // Update Livewire model if it exists
                if (this.statePath && this.$wire) {
                    this.$wire.set(this.statePath, nearest.id);
                }

                // Scroll to the selected item in the list (mobile and desktop)
                this.$nextTick(() => {
                    // For desktop list
                    const desktopList = this.$el.querySelector('.sm\\:flex .overflow-y-auto');
                    const desktopItem = desktopList?.querySelector(`li[class*="${nearest.id === this.selected ? 'bg-primary-100' : ''}"]`);

                    if (desktopList && desktopItem) {
                        desktopList.scrollTop = desktopItem.offsetTop - desktopList.offsetHeight / 2;
                    }

                    // For mobile list
                    const mobileList = this.$el.querySelector('.sm\\:hidden .overflow-y-auto');
                    const mobileItem = mobileList?.querySelector(`div[class*="${nearest.id === this.selected ? 'bg-primary-50' : ''}"]`);

                    if (mobileList && mobileItem) {
                        mobileList.scrollTop = mobileItem.offsetTop - mobileList.offsetHeight / 2;
                    }

                    // Ensure mobile sheet is open
                    const bottomSheet = this.$el.querySelector('.sm\\:hidden [x-data*="open"]');
                    if (bottomSheet && bottomSheet.__x) {
                        bottomSheet.__x.setCleanState('open', true);
                    }
                });
            }

            // Remove temporary marker after a delay
            setTimeout(() => {
                this.map.removeLayer(searchMarker);
            }, 5000);
        }, 1000);
    },
});

// --- Fix map sizing after Livewire renders ---
document.addEventListener('livewire:load', () => {
    Livewire.hook('message.processed', () => {
        document.querySelectorAll('[x-data*=locationPicker]').forEach((el) => {
            const mapContainer = el.querySelector('.map-container');
            if (mapContainer && mapContainer._leaflet_map_instance) {
                mapContainer._leaflet_map_instance.invalidateSize();
            }
        });
    });
});
